"use client";

import Link from "next/link";
import Icon from "@/components/ui/AppIcon";
import AppImage from "@/components/ui/AppImage";
import { useLanguage } from "@/context/LanguageContext";

export default function Footer() {
  const currentYear = 2026;
  const { t } = useLanguage();

  const footerLinks = {
    shop: [
      { id: "link_carpets", labelKey: "footer_carpets" as const, href: "/online-shop#carpets" },
      { id: "link_souvenirs", labelKey: "footer_souvenirs" as const, href: "/online-shop#souvenirs" },
      { id: "link_decor", labelKey: "footer_decor" as const, href: "/online-shop#decor" },
    ],
    adventures: [
      { id: "link_quad", labelKey: "footer_quad" as const, href: "/adventure-reservations#quad" },
      { id: "link_buggy", labelKey: "footer_buggy" as const, href: "/adventure-reservations#buggy" },
      { id: "link_camel", labelKey: "footer_camel" as const, href: "/adventure-reservations#camel" },
    ],
    info: [
      { id: "link_about", labelKey: "footer_about" as const, href: "/online-shop#about" },
      { id: "link_contact", labelKey: "footer_contact" as const, href: "/cart" },
      { id: "link_faq", labelKey: "footer_faq" as const, href: "/adventure-reservations#faq" },
    ],
    legal: [
      { id: "link_shipping", labelKey: "footer_shipping" as const, href: "/online-shop#products" },
      { id: "link_privacy", labelKey: "footer_privacy" as const, href: "/online-shop" },
      { id: "link_terms", labelKey: "footer_terms" as const, href: "/online-shop" },
    ],
  };

  const socialLinks = [
    { id: "social_instagram", name: "Instagram", href: "https://www.instagram.com/djerbatreasures", icon: "CameraIcon" as const },
    { id: "social_facebook", name: "Facebook", href: "https://www.facebook.com/djerbatreasures", icon: "UserGroupIcon" as const },
    { id: "social_whatsapp", name: "WhatsApp", href: "https://wa.me/21654604948", icon: "ChatBubbleLeftRightIcon" as const },
  ];

  const contactInfo = [
    { id: "contact_email", icon: "EnvelopeIcon" as const, labelKey: "footer_email_label" as const, value: "djerbatreasures@gmail.com", href: "mailto:djerbatreasures@gmail.com" },
    { id: "contact_phone", icon: "PhoneIcon" as const, labelKey: "footer_phone_label" as const, value: "+216 54 604 948", href: "tel:+21654604948" },
  ];

  return (
    <footer className="bg-secondary text-white py-16">
      <div className="container mx-auto px-6">
        {/* Contact Info Section */}
        <div className="mb-12 pb-12 border-b border-white/20">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Contact Details */}
            <div>
              <h3 className="font-semibold text-xl mb-6 text-white">{t("footer_contact_us")}</h3>
              <div className="space-y-4">
                {contactInfo.map((contact) => (
                  <a
                    key={contact.id}
                    href={contact.href}
                    className="flex items-center gap-3 text-white/80 hover:text-white transition-colors group"
                  >
                    <div className="w-10 h-10 rounded-full bg-white/10 group-hover:bg-white/20 flex items-center justify-center transition-colors">
                      <Icon name={contact.icon} size={20} className="text-white" />
                    </div>
                    <div>
                      <p className="text-xs text-white/60">{t(contact.labelKey)}</p>
                      <p className="text-sm font-medium">{contact.value}</p>
                    </div>
                  </a>
                ))}
              </div>
            </div>

            {/* Location with Google Maps */}
            <div>
              <h3 className="font-semibold text-xl mb-6 text-white">{t("footer_visit_us")}</h3>
              <div className="space-y-4">
                <div className="flex items-start gap-3 text-white/80">
                  <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
                    <Icon name="MapPinIcon" size={20} className="text-white" />
                  </div>
                  <div>
                    <p className="text-xs text-white/60 mb-1">{t("footer_location")}</p>
                    <p className="text-sm font-medium mb-3">DjerbaTreasures</p>
                    <a
                      href="https://www.google.com/maps/search/?api=1&query=DjerbaTreasures"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-sm font-medium transition-colors"
                    >
                      <Icon name="MapIcon" size={16} className="text-white" />
                      <span>{t("footer_view_maps")}</span>
                    </a>
                  </div>
                </div>

                {/* Location Photos */}
                <div className="grid grid-cols-3 gap-2 mt-4">
                  <div className="aspect-square rounded-lg overflow-hidden">
                    <AppImage
                      src="https://img.rocket.new/generatedImages/rocket_gen_img_1a8f7d90c-1770396749482.png"
                      alt="DjerbaTreasures storefront exterior view"
                      className="w-full h-full object-cover hover:scale-110 transition-transform duration-300"
                      width={400}
                      height={400}
                    />
                  </div>
                  <div className="aspect-square rounded-lg overflow-hidden">
                    <AppImage
                      src="https://img.rocket.new/generatedImages/rocket_gen_img_15b8e28b2-1764669719811.png"
                      alt="DjerbaTreasures interior with traditional Tunisian crafts and carpets"
                      className="w-full h-full object-cover hover:scale-110 transition-transform duration-300"
                      width={400}
                      height={400}
                    />
                  </div>
                  <div className="aspect-square rounded-lg overflow-hidden">
                    <AppImage
                      src="https://images.unsplash.com/photo-1585394349456-06ea3ef50a7e"
                      alt="DjerbaTreasures product display featuring handmade souvenirs and decor"
                      className="w-full h-full object-cover hover:scale-110 transition-transform duration-300"
                      width={400}
                      height={400}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Links Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
          <div>
            <h3 className="font-semibold text-lg mb-4 text-white">{t("footer_shop")}</h3>
            <ul className="space-y-2">
              {footerLinks.shop.map((link) => (
                <li key={link.id}>
                  <Link href={link.href} className="text-white/80 hover:text-white transition-colors text-sm">
                    {t(link.labelKey)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="font-semibold text-lg mb-4 text-white">{t("footer_adventures")}</h3>
            <ul className="space-y-2">
              {footerLinks.adventures.map((link) => (
                <li key={link.id}>
                  <Link href={link.href} className="text-white/80 hover:text-white transition-colors text-sm">
                    {t(link.labelKey)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="font-semibold text-lg mb-4 text-white">{t("footer_info")}</h3>
            <ul className="space-y-2">
              {footerLinks.info.map((link) => (
                <li key={link.id}>
                  <Link href={link.href} className="text-white/80 hover:text-white transition-colors text-sm">
                    {t(link.labelKey)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="font-semibold text-lg mb-4 text-white">{t("footer_legal")}</h3>
            <ul className="space-y-2">
              {footerLinks.legal.map((link) => (
                <li key={link.id}>
                  <Link href={link.href} className="text-white/80 hover:text-white transition-colors text-sm">
                    {t(link.labelKey)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-white/20 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-white/70 text-sm">
            © {currentYear} Djerba Treasures. {t("footer_rights")}
          </p>

          {/* Social Links */}
          <div className="flex items-center gap-4">
            {socialLinks.map((social) => (
              <a
                key={social.id}
                href={social.href}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                aria-label={social.name}
              >
                <Icon name={social.icon} size={20} className="text-white" />
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}